import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { FACTORY_STATUSES, FACTORY_STATUS_COLORS } from "@/constants/workflow";

const FALLBACK_COLOR = "#47505C";
const stageColor = (stage) => FACTORY_STATUS_COLORS[stage] || FALLBACK_COLOR;

/** Factory overview: one workflow status card per factory stage. */
export default function FactoryOverviewPage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/projects/${projectId}/factory/overview`)
      .then(({ data }) => {
        if (alive) setData(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [projectId]);

  const total = data?.total_panels || 0;
  const stages = data?.stages || FACTORY_STATUSES.map((s) => ({ status: s, count: 0 }));

  return (
    <div data-testid="factory-overview-page" className="p-4 sm:p-6">
      <h2 className="font-display mb-4 text-base font-semibold text-slate-100 md:text-lg">
        Workflow Status
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {stages.map((stage) => {
          const pct = total ? Math.round((stage.count / total) * 100) : 0;
          const color = stageColor(stage.status);
          return (
            <div
              key={stage.status}
              data-testid={`factory-stage-card-${stage.status.replace(/\s/g, "-")}`}
              className="rounded-xl border border-white/10 bg-[#0D1526] p-5"
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[13.5px] font-semibold text-slate-100">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  {stage.status}
                </span>
                <span className="mono text-[11px] text-slate-500">{total}</span>
              </div>

              <p className="mt-2 flex items-baseline gap-2">
                <span
                  data-testid={`factory-stage-pct-${stage.status.replace(/\s/g, "-")}`}
                  className="text-[22px] font-bold"
                  style={{ color }}
                >
                  {pct}%
                </span>
                <span className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  {stage.count}/{total} panels
                </span>
              </p>

              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/5">
                <div
                  data-testid={`factory-stage-bar-${stage.status.replace(/\s/g, "-")}`}
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
